import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    User,
    MapPin,
    Mail,
    Phone,
    School,
    CheckCircle,
    AlertCircle,
    Loader2,
    Info,
    Search,
    X,
    Globe,
    MessageCircle,
    Bell
} from 'lucide-react';
import schoolCache from '../utils/schoolCache';
import { getGeoPosition } from '../utils/getGeoPosition';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import toast from '../utils/toast';
import FormInput from '../components/FormInput';
import FormTextArea from '../components/FormTextArea';
import FormSelect from '../components/FormSelect';
import { validateForm, commonValidationRules } from '../utils/formValidation';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom user location icon
const createUserLocationIcon = () => {
    return L.divIcon({
        html: `
            <div style="
                background: #3B82F6;
                width: 40px;
                height: 40px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                border: 3px solid white;
                box-shadow: 0 2px 10px rgba(0,0,0,0.3);
                animation: pulse 2s infinite;
            ">
                <svg width="20" height="20" fill="white" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                </svg>
            </div>
            <style>
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.1); opacity: 0.7; }
                    100% { transform: scale(1); opacity: 1; }
                }
            </style>
        `,
        className: 'custom-user-marker',
        iconSize: [40, 40],
        iconAnchor: [20, 20]
    });
};

const StudentEnrollment = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        parentsEmail: '',
        motherFirstName: '',
        motherLastName: '',
        motherEmail: '',
        motherPhone: '',
        fatherFirstName: '',
        fatherLastName: '',
        fatherEmail: '',
        fatherPhone: '',
        schoolId: '',
        latitude: null,
        longitude: null,
        accuracy: null,
        allowEmailUpdates: true,
        allowSMSUpdates: false
    });

    const [selectedSchool, setSelectedSchool] = useState(null);
    const [schoolSearch, setSchoolSearch] = useState('');
    const [schoolResults, setSchoolResults] = useState([]);
    const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [allSchools, setAllSchools] = useState([]);
    const [schoolsLoaded, setSchoolsLoaded] = useState(false);
    const [geolocationStatus, setGeolocationStatus] = useState('idle'); // idle, pending, approved, denied, error
    const [geolocationError, setGeolocationError] = useState('');
    const [errors, setErrors] = useState({});
    const [submitStatus, setSubmitStatus] = useState(''); // success, error

    // Test function for console debugging
    const testGeolocation = () => {
        console.log('🌍 [TEST] Testing geolocation directly...');
        if (!navigator.geolocation) {
            console.error('🌍 [TEST] ❌ Geolocation not supported');
            return;
        }

        console.log('🌍 [TEST] 🔍 Testing with different options...');

        // Test 1: Basic options
        console.log('🌍 [TEST] Test 1: Basic options');
        navigator.geolocation.getCurrentPosition(
            (pos) => console.log('🌍 [TEST] ✅ Basic Success:', pos.coords),
            (err) => console.error('🌍 [TEST] ❌ Basic Error:', err),
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );

        // Test 2: High accuracy
        setTimeout(() => {
            console.log('🌍 [TEST] Test 2: High accuracy');
            navigator.geolocation.getCurrentPosition(
                (pos) => console.log('🌍 [TEST] ✅ High Accuracy Success:', pos.coords),
                (err) => console.error('🌍 [TEST] ❌ High Accuracy Error:', err),
                { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
            );
        }, 2000);

        // Test 3: Very permissive
        setTimeout(() => {
            console.log('🌍 [TEST] Test 3: Very permissive');
            navigator.geolocation.getCurrentPosition(
                (pos) => console.log('🌍 [TEST] ✅ Permissive Success:', pos.coords),
                (err) => console.error('🌍 [TEST] ❌ Permissive Error:', err),
                { enableHighAccuracy: false, timeout: 30000, maximumAge: 600000 }
            );
        }, 4000);
    };

    // IP-based geolocation fallback
    const testIPGeolocation = async () => {
        console.log('🌍 [IP] Testing IP-based geolocation...');
        try {
            const response = await fetch('https://ipapi.co/json/');
            const data = await response.json();
            console.log('🌍 [IP] IP Geolocation result:', data);

            if (data.latitude && data.longitude) {
                console.log('🌍 [IP] ✅ IP coordinates found:', {
                    latitude: data.latitude,
                    longitude: data.longitude,
                    city: data.city,
                    country: data.country_name
                });

                // Populate the form with IP-based location
                setFormData(prev => ({
                    ...prev,
                    latitude: data.latitude,
                    longitude: data.longitude,
                    accuracy: 10000 // IP geolocation is typically accurate to ~10km
                }));
                setGeolocationStatus('approved');
                console.log('🌍 [IP] ✅ Form populated with IP location');
            }
        } catch (error) {
            console.error('🌍 [IP] ❌ IP geolocation failed:', error);
        }
    };

    // Expose test functions to window for console debugging
    React.useEffect(() => {
        window.testGeolocation = testGeolocation;
        window.testIPGeolocation = testIPGeolocation;
        return () => {
            delete window.testGeolocation;
            delete window.testIPGeolocation;
        };
    }, []);

    // Simple, direct geolocation implementation
    const requestGeolocation = async (e) => {
        console.log('🌍 [STUDENT] 🚀 requestGeolocation called by user click');

        setGeolocationError('');
        setGeolocationStatus('pending');
        console.log('🌍 [STUDENT] ⏳ Set status to pending');

        if (!navigator.geolocation) {
            console.error('🌍 [STUDENT] ❌ Geolocation not supported');
            setGeolocationStatus('error');
            setGeolocationError('Geolocation is not supported by this browser.');
            return;
        }

        // Try the simplest possible approach first
        console.log('🌍 [STUDENT] 🎯 Trying simple geolocation...');
        navigator.geolocation.getCurrentPosition(
            (position) => {
                console.log('🌍 [STUDENT] ✅ Simple geolocation success:', {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                });

                setFormData(prev => ({
                    ...prev,
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                }));
                setGeolocationStatus('approved');
                console.log('🌍 [STUDENT] ✅ Set status to approved');
            },
            (error) => {
                console.error('🌍 [STUDENT] ❌ Simple geolocation failed:', error);
                console.error('🌍 [STUDENT] ❌ Error code:', error.code);
                console.error('🌍 [STUDENT] ❌ Error message:', error.message);

                // If simple fails, try IP geolocation as fallback
                console.log('🌍 [STUDENT] 🔄 Trying IP geolocation fallback...');
                testIPGeolocation();
            },
            {
                enableHighAccuracy: false,
                timeout: 20000,
                maximumAge: 300000 // 5 minutes cache
            }
        );
    };

    // Geolocation effect - don't auto-request, wait for user action
    useEffect(() => {
        console.log('🌍 [STUDENT] Geolocation effect triggered on page load');

        if (!navigator.geolocation) {
            console.error('🌍 [STUDENT] ❌ Geolocation not supported');
            setGeolocationStatus('error');
            setGeolocationError('Geolocation is not supported by this browser.');
            return;
        }
        console.log('🌍 [STUDENT] ✅ Geolocation API available');

        if (!window.isSecureContext) {
            console.error('🌍 [STUDENT] ❌ Not in secure context:', window.location.protocol);
            setGeolocationStatus('error');
            setGeolocationError('Geolocation requires a secure connection (HTTPS). Please access this site via https://amhas.org');
            return;
        }
        console.log('🌍 [STUDENT] ✅ Secure context confirmed');

        // Check if we're in an iframe and geolocation isn't allowed
        const inIframe = window.top !== window.self;
        const geoAllowed = document.permissionsPolicy?.allowsFeature?.('geolocation') ?? true;
        console.log('🌍 [STUDENT] In iframe:', inIframe, 'Geo allowed:', geoAllowed);

        if (inIframe && !geoAllowed) {
            console.error('🌍 [STUDENT] ❌ Blocked by iframe/policy');
            setGeolocationStatus('error');
            setGeolocationError('This page is embedded in a site that blocks location. Open it directly at https://amhas.org or allow geolocation for the embed.');
            return;
        }

        // Check if geolocation is already approved
        console.log('🌍 [STUDENT] 🔍 Checking permission state...');
        if (navigator.permissions?.query) {
            navigator.permissions.query({ name: 'geolocation' }).then((status) => {
                console.log('🌍 [STUDENT] 🔍 Permission state:', status.state);
                if (status.state === 'granted') {
                    console.log('🌍 [STUDENT] ✅ Permission already granted');
                    setGeolocationStatus('approved');
                } else if (status.state === 'denied') {
                    console.log('🌍 [STUDENT] ❌ Permission denied');
                    setGeolocationStatus('denied');
                    setGeolocationError('Location access denied. Please click the location icon in your browser address bar and allow access, then try again.');
                } else {
                    console.log('🌍 [STUDENT] ⏳ Permission pending');
                    setGeolocationStatus('idle');
                }
            }).catch((err) => {
                console.log('🌍 [STUDENT] 🔍 Permission query failed:', err);
                // Permissions API not supported, start with idle
                setGeolocationStatus('idle');
            });
        } else {
            console.log('🌍 [STUDENT] 🔍 Permissions API not available, starting with idle');
            setGeolocationStatus('idle');
        }
    }, []);

    // Load all schools from cache
    useEffect(() => {
        const unsubscribe = schoolCache.subscribe((cacheData) => {
            setAllSchools(cacheData.schools || []);
            setSchoolsLoaded(!cacheData.isLoading);
        });

        // Load schools if not already loaded
        if (schoolCache.schools.length === 0) {
            schoolCache.loadSchools();
        } else {
            setAllSchools(schoolCache.schools);
            setSchoolsLoaded(true);
        }

        return unsubscribe;
    }, []);

    // Client-side school search effect using cache with incremental paging
    const [searchPage, setSearchPage] = useState(1);
    const resultsPerPage = 20;

    useEffect(() => {
        if (!schoolsLoaded || schoolSearch.length < 1) {
            setSchoolResults([]);
            setShowSchoolDropdown(false);
            setSearchPage(1);
            return;
        }

        const pageSize = searchPage * resultsPerPage;
        const filteredSchools = schoolCache.searchSchools(schoolSearch, pageSize);
        setSchoolResults(filteredSchools);
        setShowSchoolDropdown(filteredSchools.length > 0);
    }, [schoolSearch, schoolsLoaded, searchPage]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSchoolSelect = (school) => {
        setSelectedSchool(school);
        setFormData(prev => ({ ...prev, schoolId: school.id }));
        setSchoolSearch(school.name);
        setShowSchoolDropdown(false);
        if (errors.schoolId) {
            setErrors(prev => ({ ...prev, schoolId: null }));
        }
    };


    const validateFormData = () => {
        const newErrors = {};

        // Use standardized validation for core fields
        const coreErrors = validateForm(formData, commonValidationRules.studentEnrollment);
        Object.assign(newErrors, coreErrors);

        // Additional validation for parent fields
        if (!formData.motherFirstName?.trim()) newErrors.motherFirstName = 'Mother first name is required';
        if (!formData.motherLastName?.trim()) newErrors.motherLastName = 'Mother last name is required';
        if (!formData.motherEmail?.trim()) newErrors.motherEmail = 'Mother email is required';
        if (!formData.motherPhone?.trim()) newErrors.motherPhone = 'Mother phone is required';
        if (!formData.fatherFirstName?.trim()) newErrors.fatherFirstName = 'Father first name is required';
        if (!formData.fatherLastName?.trim()) newErrors.fatherLastName = 'Father last name is required';
        if (!formData.fatherEmail?.trim()) newErrors.fatherEmail = 'Father email is required';
        if (!formData.fatherPhone?.trim()) newErrors.fatherPhone = 'Father phone is required';
        if (!formData.schoolId) newErrors.schoolId = 'School selection is required';

        // Email validation for parent fields
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.motherEmail && !emailRegex.test(formData.motherEmail)) {
            newErrors.motherEmail = 'Please enter a valid email address';
        }
        if (formData.fatherEmail && !emailRegex.test(formData.fatherEmail)) {
            newErrors.fatherEmail = 'Please enter a valid email address';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateFormData()) {
            return;
        }

        setLoading(true);
        setSubmitStatus('');

        try {
            const response = await fetch('/api/students', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                const result = await response.json();
                toast.success('Student enrollment successful!');

                // Navigate to success page
                navigate('/enrollment-success', {
                    state: {
                        enrollmentType: 'student',
                        studentName: `${formData.firstName} ${formData.lastName}`,
                        schoolName: selectedSchool?.name
                    }
                });
            } else if (response.status === 429) {
                toast.error('Too many requests. Please wait a moment and try again.');
                setSubmitStatus('error');
            } else {
                try {
                    const errorData = await response.json();
                    console.error('Enrollment error:', errorData);

                    // Show error toast
                    toast.error(errorData.error || 'Failed to submit enrollment. Please try again.');

                    setSubmitStatus('error');
                    if (errorData.details) {
                        const fieldErrors = {};
                        errorData.details.forEach(error => {
                            // Map error messages to form fields
                            if (error.includes('First name')) fieldErrors.firstName = error;
                            else if (error.includes('Last name')) fieldErrors.lastName = error;
                            else if (error.includes('parent email')) fieldErrors.parentsEmail = error;
                            else if (error.includes('Mother')) {
                                if (error.includes('email')) fieldErrors.motherEmail = error;
                                else if (error.includes('phone')) fieldErrors.motherPhone = error;
                                else if (error.includes('first name')) fieldErrors.motherFirstName = error;
                                else if (error.includes('last name')) fieldErrors.motherLastName = error;
                            }
                            else if (error.includes('Father')) {
                                if (error.includes('email')) fieldErrors.fatherEmail = error;
                                else if (error.includes('phone')) fieldErrors.fatherPhone = error;
                                else if (error.includes('first name')) fieldErrors.fatherFirstName = error;
                                else if (error.includes('last name')) fieldErrors.fatherLastName = error;
                            }
                            else if (error.includes('School')) fieldErrors.schoolId = error;
                        });
                        setErrors(fieldErrors);
                    }
                } catch (parseError) {
                    console.error('Error parsing response:', parseError);
                    toast.error('Server error. Please try again later.');
                    setSubmitStatus('error');
                }
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('Network error. Please check your connection and try again.');
            setSubmitStatus('error');
        } finally {
            setLoading(false);
        }
    };

    const Tooltip = ({ children, text }) => (
        <div className="relative group">
            {children}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
                {text}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pt-24 pb-12">
            <div className="container-custom">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-2xl mx-auto"
                >
                    {/* Logo and Title */}
                    <div className="text-center mb-12">
                        <motion.img
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.6 }}
                            src="/amhas-logo.png"
                            alt="AMHAS Logo"
                            className="w-24 h-24 mx-auto mb-4"
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                                AMHAS
                            </h1>
                            <h2 className="text-3xl md:text-4xl font-bold text-primary-600 mb-4">
                                Student Enrollment Form
                            </h2>
                            <p className="text-lg text-gray-600 max-w-xl mx-auto">
                                Join our early sign-up list! You're not committing to anything - just securing your spot
                                for when we launch. Early sign-ups get a 1,200 CFA discount and help us prioritize
                                which regions to launch first.
                            </p>
                        </motion.div>
                    </div>

                    {/* Geolocation Status */}
                    {geolocationStatus !== 'approved' && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className={`mb-8 p-4 rounded-lg border-2 ${geolocationStatus === 'denied' || geolocationStatus === 'error'
                                ? 'bg-red-50 border-red-200'
                                : 'bg-yellow-50 border-yellow-200'
                                }`}
                        >
                            <div className="flex items-center space-x-3">
                                <div className={`p-2 rounded-full ${geolocationStatus === 'denied' || geolocationStatus === 'error'
                                    ? 'bg-red-100'
                                    : 'bg-yellow-100'
                                    }`}>
                                    {geolocationStatus === 'pending' ? (
                                        <Loader2 size={20} className="text-yellow-600 animate-spin" />
                                    ) : (
                                        <AlertCircle size={20} className={
                                            geolocationStatus === 'denied' || geolocationStatus === 'error'
                                                ? 'text-red-600'
                                                : 'text-yellow-600'
                                        } />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900">
                                        Location Permission Required
                                    </h3>
                                    <p className="text-sm text-gray-600 mb-2">
                                        {geolocationError || 'We need your location to help us plan regional launches.'}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            console.log('🌍 [STUDENT] 🔘 Button clicked!');
                                            requestGeolocation(e);
                                        }}
                                        className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-2"
                                        disabled={geolocationStatus === 'pending' || geolocationStatus === 'approved'}
                                    >
                                        {geolocationStatus === 'pending' ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />}
                                        {geolocationStatus === 'pending' ? 'Getting location…' :
                                            geolocationStatus === 'approved' ? 'Location Enabled' : 'Enable Location'}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Location Display */}
                    {geolocationStatus === 'approved' && formData.latitude && formData.longitude && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="mb-8"
                        >
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className="p-2 bg-green-100 rounded-full">
                                        <MapPin size={20} className="text-green-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-green-900">Location Retrieved Successfully</h3>
                                        <p className="text-sm text-green-700">
                                            Latitude: {formData.latitude.toFixed(6)}, Longitude: {formData.longitude.toFixed(6)}
                                        </p>
                                    </div>
                                </div>

                                {/* Interactive Map */}
                                <div className="w-full aspect-square rounded-lg overflow-hidden">
                                    <MapContainer
                                        center={[formData.latitude, formData.longitude]}
                                        zoom={15}
                                        style={{ height: '100%', width: '100%' }}
                                        className="rounded-lg"
                                    >
                                        <TileLayer
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        />
                                        <Marker
                                            position={[formData.latitude, formData.longitude]}
                                            icon={createUserLocationIcon()}
                                        >
                                            <Popup>
                                                <div className="text-center">
                                                    <h3 className="font-semibold text-gray-900">Your Location</h3>
                                                    <p className="text-sm text-gray-600">
                                                        {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                                                    </p>
                                                    {formData.accuracy && (
                                                        <p className="text-xs text-gray-500">
                                                            Accuracy: ±{formData.accuracy.toFixed(0)}m
                                                        </p>
                                                    )}
                                                </div>
                                            </Popup>
                                        </Marker>
                                    </MapContainer>
                                </div>

                                <div className="mt-4 flex justify-between items-center">
                                    <button
                                        type="button"
                                        onClick={requestGeolocation}
                                        className="text-sm text-green-700 hover:text-green-800 underline"
                                    >
                                        Update Location
                                    </button>
                                    <span className="text-xs text-green-600">
                                        Accuracy: ±{(formData.accuracy || 0).toFixed(0)}m
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Form Container with Animated Border */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="relative"
                    >

                        <div className="relative bg-white rounded-2xl p-8 m-1">
                            {/* Success Message */}
                            {submitStatus === 'success' && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
                                >
                                    <div className="flex items-center space-x-2">
                                        <CheckCircle size={20} className="text-green-600" />
                                        <span className="text-green-800 font-medium">
                                            Enrollment submitted successfully! You'll receive a confirmation email soon.
                                        </span>
                                    </div>
                                </motion.div>
                            )}

                            {/* Error Message */}
                            {submitStatus === 'error' && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
                                >
                                    <div className="flex items-center space-x-2">
                                        <AlertCircle size={20} className="text-red-600" />
                                        <span className="text-red-800 font-medium">
                                            There was an error submitting your enrollment. Please check your information and try again.
                                        </span>
                                    </div>
                                </motion.div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Student Information */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                        <User size={20} className="text-primary-600" />
                                        <span>Student Information</span>
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <Tooltip text="Enter the student's first name as it appears on official documents">
                                            <FormInput
                                                label="Student First Name"
                                                name="firstName"
                                                type="text"
                                                value={formData.firstName}
                                                onChange={handleInputChange}
                                                placeholder="Enter first name"
                                                required={true}
                                                error={errors.firstName}
                                            />
                                        </Tooltip>

                                        <Tooltip text="Enter the student's last name as it appears on official documents">
                                            <FormInput
                                                label="Student Last Name"
                                                name="lastName"
                                                type="text"
                                                value={formData.lastName}
                                                onChange={handleInputChange}
                                                placeholder="Enter last name"
                                                required={true}
                                                error={errors.lastName}
                                            />
                                        </Tooltip>
                                    </div>

                                    <Tooltip text="Primary email for important communications and updates">
                                        <FormInput
                                            label="Parents Email"
                                            name="parentsEmail"
                                            type="email"
                                            value={formData.parentsEmail}
                                            onChange={handleInputChange}
                                            placeholder="parent@example.com"
                                            required={true}
                                            error={errors.parentsEmail}
                                        />
                                    </Tooltip>
                                </div>

                                {/* Mother Information */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                        <User size={20} className="text-accent-600" />
                                        <span>Mother Information</span>
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Tooltip text="Mother's first name for contact purposes">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    First Name *
                                                    <Info size={14} className="inline ml-1 text-gray-400" />
                                                </label>
                                            </Tooltip>
                                            <input
                                                type="text"
                                                name="motherFirstName"
                                                value={formData.motherFirstName}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.motherFirstName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter mother's first name"
                                            />
                                            {errors.motherFirstName && (
                                                <p className="text-red-600 text-sm mt-1">{errors.motherFirstName}</p>
                                            )}
                                        </div>

                                        <div>
                                            <Tooltip text="Mother's last name for contact purposes">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Last Name *
                                                    <Info size={14} className="inline ml-1 text-gray-400" />
                                                </label>
                                            </Tooltip>
                                            <input
                                                type="text"
                                                name="motherLastName"
                                                value={formData.motherLastName}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.motherLastName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter mother's last name"
                                            />
                                            {errors.motherLastName && (
                                                <p className="text-red-600 text-sm mt-1">{errors.motherLastName}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Tooltip text="Mother's email for direct communication">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Email *
                                                    <Info size={14} className="inline ml-1 text-gray-400" />
                                                </label>
                                            </Tooltip>
                                            <input
                                                type="email"
                                                name="motherEmail"
                                                value={formData.motherEmail}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.motherEmail ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="mother@example.com"
                                            />
                                            {errors.motherEmail && (
                                                <p className="text-red-600 text-sm mt-1">{errors.motherEmail}</p>
                                            )}
                                        </div>

                                        <div>
                                            <Tooltip text="Mother's phone number for urgent communications">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Phone *
                                                    <Info size={14} className="inline ml-1 text-gray-400" />
                                                </label>
                                            </Tooltip>
                                            <input
                                                type="tel"
                                                name="motherPhone"
                                                value={formData.motherPhone}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.motherPhone ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="+220 XXX XXXX"
                                            />
                                            {errors.motherPhone && (
                                                <p className="text-red-600 text-sm mt-1">{errors.motherPhone}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Father Information */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                        <User size={20} className="text-secondary-600" />
                                        <span>Father Information</span>
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Tooltip text="Father's first name for contact purposes">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    First Name *
                                                    <Info size={14} className="inline ml-1 text-gray-400" />
                                                </label>
                                            </Tooltip>
                                            <input
                                                type="text"
                                                name="fatherFirstName"
                                                value={formData.fatherFirstName}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.fatherFirstName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter father's first name"
                                            />
                                            {errors.fatherFirstName && (
                                                <p className="text-red-600 text-sm mt-1">{errors.fatherFirstName}</p>
                                            )}
                                        </div>

                                        <div>
                                            <Tooltip text="Father's last name for contact purposes">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Last Name *
                                                    <Info size={14} className="inline ml-1 text-gray-400" />
                                                </label>
                                            </Tooltip>
                                            <input
                                                type="text"
                                                name="fatherLastName"
                                                value={formData.fatherLastName}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.fatherLastName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter father's last name"
                                            />
                                            {errors.fatherLastName && (
                                                <p className="text-red-600 text-sm mt-1">{errors.fatherLastName}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Tooltip text="Father's email for direct communication">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Email *
                                                    <Info size={14} className="inline ml-1 text-gray-400" />
                                                </label>
                                            </Tooltip>
                                            <input
                                                type="email"
                                                name="fatherEmail"
                                                value={formData.fatherEmail}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.fatherEmail ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="father@example.com"
                                            />
                                            {errors.fatherEmail && (
                                                <p className="text-red-600 text-sm mt-1">{errors.fatherEmail}</p>
                                            )}
                                        </div>

                                        <div>
                                            <Tooltip text="Father's phone number for urgent communications">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Phone *
                                                    <Info size={14} className="inline ml-1 text-gray-400" />
                                                </label>
                                            </Tooltip>
                                            <input
                                                type="tel"
                                                name="fatherPhone"
                                                value={formData.fatherPhone}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.fatherPhone ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="+220 XXX XXXX"
                                            />
                                            {errors.fatherPhone && (
                                                <p className="text-red-600 text-sm mt-1">{errors.fatherPhone}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* School Selection */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                        <School size={20} className="text-primary-600" />
                                        <span>School Information</span>
                                    </h3>

                                    <div className="relative">
                                        <Tooltip text="Search and select your child's school from our database">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                School Name *
                                                <Info size={14} className="inline ml-1 text-gray-400" />
                                            </label>
                                        </Tooltip>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={schoolSearch}
                                                onChange={(e) => setSchoolSearch(e.target.value)}
                                                onFocus={() => schoolResults.length > 0 && setShowSchoolDropdown(true)}
                                                className={`w-full px-4 py-3 pl-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.schoolId ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Search for your school..."
                                            />
                                            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                            {selectedSchool && !showSchoolDropdown && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedSchool(null);
                                                        setFormData(prev => ({ ...prev, schoolId: '' }));
                                                        setSchoolSearch('');
                                                    }}
                                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                >
                                                    <X size={20} />
                                                </button>
                                            )}
                                        </div>

                                        {/* School Dropdown */}
                                        {showSchoolDropdown && schoolResults.length > 0 && (
                                            <div
                                                className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto custom-scrollbar"
                                                onScroll={(e) => {
                                                    const el = e.currentTarget;
                                                    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
                                                        setSearchPage(prev => prev + 1);
                                                    }
                                                }}
                                            >
                                                {schoolResults.map((school) => (
                                                    <button
                                                        key={school.id}
                                                        type="button"
                                                        onClick={() => handleSchoolSelect(school)}
                                                        className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                                                    >
                                                        <div className="font-medium text-gray-900">{school.name}</div>
                                                        <div className="text-sm text-gray-600">{school.district}, {school.region}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {/* No Results - Create School Link */}
                                        {showSchoolDropdown && schoolResults.length === 0 && schoolSearch.length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                                                <div className="p-4 text-center">
                                                    <p className="text-gray-600 mb-3">No schools found matching "{schoolSearch}"</p>
                                                    <a
                                                        href="/school-enrollment"
                                                        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                                                    >
                                                        <School size={16} className="mr-2" />
                                                        Create New School
                                                    </a>
                                                </div>
                                            </div>
                                        )}

                                        {errors.schoolId && (
                                            <p className="text-red-600 text-sm mt-1">{errors.schoolId}</p>
                                        )}

                                        {selectedSchool && (
                                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                                <div className="flex items-center space-x-2">
                                                    <CheckCircle size={16} className="text-green-600" />
                                                    <span className="text-green-800 font-medium">Selected School:</span>
                                                </div>
                                                <div className="mt-1 text-sm text-gray-700">
                                                    <div>{selectedSchool.name}</div>
                                                    <div>{selectedSchool.district}, {selectedSchool.region}</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Location Information */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                        <MapPin size={20} className="text-primary-600" />
                                        <span>Location Information</span>
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Latitude (X Coordinate)
                                            </label>
                                            <input
                                                type="number"
                                                step="any"
                                                name="latitude"
                                                value={formData.latitude || ''}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                                placeholder="Enter latitude (e.g., 13.4549)"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Longitude (Y Coordinate)
                                            </label>
                                            <input
                                                type="number"
                                                step="any"
                                                name="longitude"
                                                value={formData.longitude || ''}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                                placeholder="Enter longitude (e.g., -16.5790)"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-4">
                                        <button
                                            type="button"
                                            onClick={requestGeolocation}
                                            disabled={geolocationStatus === 'pending'}
                                            className="btn-secondary text-sm py-2 px-4 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {geolocationStatus === 'pending' ? (
                                                <>
                                                    <Loader2 size={16} className="animate-spin" />
                                                    <span>Getting Location...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <MapPin size={16} />
                                                    <span>Get Current Location</span>
                                                </>
                                            )}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={testIPGeolocation}
                                            className="btn-outline text-sm py-2 px-4 inline-flex items-center gap-2"
                                        >
                                            <Globe size={16} />
                                            <span>Use IP Location</span>
                                        </button>

                                        {formData.accuracy && (
                                            <span className="text-sm text-gray-600">
                                                Accuracy: ±{formData.accuracy.toFixed(0)}m
                                            </span>
                                        )}
                                    </div>

                                    <p className="text-sm text-gray-600">
                                        Enter your coordinates manually or use the button to get your current location automatically.
                                    </p>
                                </div>

                                {/* Communication Preferences */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                                        <Bell size={20} className="text-primary-600" />
                                        <span>Communication Preferences</span>
                                    </h3>

                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <p className="text-sm text-blue-800 mb-4">
                                            Help us keep you informed about our launch progress and when services become available in your area.
                                        </p>

                                        <div className="space-y-3">
                                            <label className="flex items-center space-x-3">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.allowEmailUpdates}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        allowEmailUpdates: e.target.checked
                                                    }))}
                                                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                                                />
                                                <div className="flex items-center space-x-2">
                                                    <Mail size={16} className="text-blue-600" />
                                                    <span className="text-sm font-medium text-gray-700">
                                                        Email Updates
                                                    </span>
                                                </div>
                                            </label>
                                            <p className="text-xs text-gray-600 ml-7">
                                                Receive updates about launch progress, regional availability, and service announcements
                                            </p>

                                            <label className="flex items-center space-x-3">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.allowSMSUpdates}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        allowSMSUpdates: e.target.checked
                                                    }))}
                                                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                                                />
                                                <div className="flex items-center space-x-2">
                                                    <MessageCircle size={16} className="text-green-600" />
                                                    <span className="text-sm font-medium text-gray-700">
                                                        SMS Updates
                                                    </span>
                                                </div>
                                            </label>
                                            <p className="text-xs text-gray-600 ml-7">
                                                Receive important notifications via text message (standard messaging rates may apply)
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Consent Checkbox */}
                                <div className="space-y-4">
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                        <label className="flex items-start space-x-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                required
                                                className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 mt-1"
                                            />
                                            <div className="text-sm text-gray-700">
                                                <span className="font-medium">I agree to receive promotional materials, updates, and communications</span>
                                                <p className="text-gray-600 mt-1">
                                                    By checking this box, you consent to receive emails, text messages, and promotional materials about AMHAS services, launch updates, and educational content. You can unsubscribe at any time.
                                                </p>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="pt-6">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full btn-primary py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 size={20} className="animate-spin" />
                                                <span>Submitting Enrollment...</span>
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle size={20} />
                                                <span>Submit Early Sign-Up</span>
                                            </>
                                        )}
                                    </button>

                                    <p className="text-center text-sm text-gray-600 mt-4">
                                        By submitting, you're joining our early sign-up list for a guaranteed spot and 1,200 CFA discount when we launch.
                                    </p>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default StudentEnrollment;
